import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingIndicator } from '../../components/Spinner';
import { formatDate } from '../../i18n';
import type { TimeWindow } from './api';
import {
  useCalendars,
  useCreateCalendar,
  useCreateHoliday,
  useDeleteCalendar,
  useUpdateCalendar,
  useDeleteHoliday,
  useHolidays,
} from './queries';

/**
 * Geschaeftszeiten und Feiertage -- F4-01, K-117.
 *
 * Die Zeitfenster werden je Wochentag gepflegt, nicht als Freitext. Der
 * Server nimmt auch eine Liste entgegen, aber ein Eingabefeld fuer
 * `[{"weekday": 0, ...}]` waere eine Zumutung -- und jeder Tippfehler darin
 * eine falsch berechnete Frist.
 */

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const;

interface DayRow {
  enabled: boolean;
  start: string;
  end: string;
}

const DEFAULT_ROW: DayRow = { enabled: false, start: '08:00', end: '17:00' };

/** Montag bis Freitag, 8 bis 17 Uhr -- der Ausgangspunkt fuer einen neuen Kalender. */
function defaultRows(): DayRow[] {
  return WEEKDAYS.map((day) => ({ ...DEFAULT_ROW, enabled: day <= 4 }));
}

/**
 * Die Gegenrichtung: aus den Fenstern des Servers die Zeilen der Maske.
 *
 * Ein Tag ohne Fenster ist ein freier Tag -- nicht ein Tag mit leeren
 * Uhrzeiten. Deshalb setzt sie die Vorgabezeiten und schaltet nur die Tage
 * ein, zu denen es ein Fenster gibt: Wer den Samstag hinzunimmt, findet dort
 * "08:00 bis 17:00" vor und nicht zwei leere Felder.
 */
function fromWindows(windows: TimeWindow[]): DayRow[] {
  const rows = WEEKDAYS.map(() => ({ ...DEFAULT_ROW }));
  for (const window of windows) {
    rows[window.weekday] = {
      enabled: true,
      start: window.start.slice(0, 5),
      end: window.end.slice(0, 5),
    };
  }
  return rows;
}

function toWindows(rows: DayRow[]): TimeWindow[] {
  return rows
    .map((row, weekday) => ({ weekday, start: row.start, end: row.end, enabled: row.enabled }))
    .filter((row) => row.enabled)
    .map(({ weekday, start, end }) => ({ weekday, start, end }));
}

export function CalendarPanel() {
  const { t, i18n } = useTranslation();
  const calendars = useCalendars();
  const create = useCreateCalendar();
  const remove = useDeleteCalendar();
  const update = useUpdateCalendar();

  const [name, setName] = useState('');
  const [rows, setRows] = useState<DayRow[]>(defaultRows);
  const [selected, setSelected] = useState<string | null>(null);

  // Welcher Kalender gerade bearbeitet wird -- ``null`` heisst: ein neuer.
  //
  // **Bearbeiten statt loeschen und neu anlegen.** Servicezusagen und
  // Warteschlangen verweisen auf den Kalender; ein Loeschen ist damit
  // entweder gesperrt oder reisst die Zusage mit. Wer die Oeffnungszeiten um
  // eine halbe Stunde verschieben will, soll das tun koennen.
  const [editing, setEditing] = useState<string | null>(null);

  const startEditing = (calendar: { id: string; name: string; workdays: TimeWindow[] }) => {
    setEditing(calendar.id);
    setName(calendar.name);
    setRows(fromWindows(calendar.workdays));
  };

  const stopEditing = () => {
    setEditing(null);
    setName('');
    setRows(defaultRows());
  };

  return (
    <div className="detail-grid">
      <div className="stack">
        <section className="card">
          <h2>{editing === null ? t('sla.newCalendar') : t('sla.editCalendar')}</h2>

          <div className="field">
            <label className="field__label" htmlFor="calendar-name">
              {t('masterdata.name')}
            </label>
            <input
              id="calendar-name"
              className="field__control"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
          </div>

          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('sla.weekday')}</th>
                <th scope="col">{t('sla.from')}</th>
                <th scope="col">{t('sla.to')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, weekday) => (
                <tr key={weekday}>
                  <td>
                    <label className="field__checkbox">
                      <input
                        type="checkbox"
                        checked={row.enabled}
                        onChange={(event) => {
                          setRows((current) =>
                            current.map((item, index) =>
                              index === weekday ? { ...item, enabled: event.target.checked } : item,
                            ),
                          );
                        }}
                      />
                      {t(`weekday.${weekday}`)}
                    </label>
                  </td>
                  <td>
                    <input
                      type="time"
                      className="field__control"
                      value={row.start}
                      disabled={!row.enabled}
                      onChange={(event) => {
                        setRows((current) =>
                          current.map((item, index) =>
                            index === weekday ? { ...item, start: event.target.value } : item,
                          ),
                        );
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      className="field__control"
                      value={row.end}
                      disabled={!row.enabled}
                      onChange={(event) => {
                        setRows((current) =>
                          current.map((item, index) =>
                            index === weekday ? { ...item, end: event.target.value } : item,
                          ),
                        );
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {create.isError ? <ErrorNotice error={create.error} /> : null}
          {update.isError ? <ErrorNotice error={update.error} /> : null}

          <div className="row">
            <Button
              variant="primary"
              disabled={!name.trim()}
              busy={create.isPending || update.isPending}
              onClick={() => {
                const payload = {
                  name: name.trim(),
                  timezone: 'Europe/Berlin',
                  workdays: toWindows(rows),
                };
                if (editing !== null) {
                  update.mutate({ id: editing, payload }, { onSuccess: stopEditing });
                  return;
                }
                create.mutate(payload, { onSuccess: stopEditing });
              }}
            >
              {editing === null ? t('masterdata.add') : t('app.save')}
            </Button>
            {editing !== null ? (
              <Button variant="ghost" onClick={stopEditing}>
                {t('app.cancel')}
              </Button>
            ) : null}
          </div>
        </section>

        <section className="card card--flush">
          {calendars.isPending ? <LoadingIndicator /> : null}
          {calendars.isError ? <ErrorNotice error={calendars.error} /> : null}

          {calendars.data && calendars.data.length > 0 ? (
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t('masterdata.name')}</th>
                    <th scope="col">{t('sla.timezone')}</th>
                    <th scope="col">{t('sla.workdays')}</th>
                    <th scope="col">{t('masterdata.isDefault')}</th>
                    <th scope="col">{t('tickets.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {calendars.data.map((calendar) => (
                    <tr key={calendar.id}>
                      <td>
                        <button
                          type="button"
                          className="table__subject"
                          onClick={() => {
                            setSelected(calendar.id === selected ? null : calendar.id);
                          }}
                        >
                          {calendar.name}
                        </button>
                      </td>
                      <td className="table__muted">{calendar.timezone}</td>
                      <td className="table__muted">
                        {calendar.workdays
                          .map(
                            (window) =>
                              `${t(`weekday.${window.weekday}`)} ${window.start.slice(0, 5)}–${window.end.slice(0, 5)}`,
                          )
                          .join(', ') || t('app.none')}
                      </td>
                      <td>
                        {calendar.is_default ? (
                          <Badge tone="info">{t('masterdata.yes')}</Badge>
                        ) : (
                          <span className="table__muted">{t('masterdata.no')}</span>
                        )}
                      </td>
                      <td>
                        {/* Bearbeiten vor Loeschen -- F4-01. Zusagen und
                            Warteschlangen verweisen auf den Kalender; ihn
                            zum Aendern der Oeffnungszeiten wegzuwerfen ist
                            entweder gesperrt oder reisst sie mit. */}
                        <Button
                          variant="ghost"
                          onClick={() => {
                            startEditing(calendar);
                          }}
                        >
                          {t('masterdata.edit')}
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            remove.mutate(calendar.id);
                          }}
                        >
                          {t('masterdata.delete')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {remove.isError ? <ErrorNotice error={remove.error} /> : null}
        </section>
      </div>

      <aside className="card">
        <h2>{t('sla.holidays')}</h2>
        <p className="table__muted">
          {selected ? t('sla.holidaysHint') : t('sla.selectCalendarFirst')}
        </p>
        {selected ? <HolidayEditor calendarId={selected} language={i18n.language} /> : null}
      </aside>
    </div>
  );
}

/**
 * Die Feiertage eines Kalenders.
 *
 * Ein wiederkehrender Feiertag traegt trotzdem ein Datum: Von ihm zaehlen nur
 * Tag und Monat. Das ist einfacher zu erklaeren als zwei Eingabefelder -- und
 * bei den beweglichen Feiertagen ist das Datum ohnehin das, was eingetragen
 * wird.
 */
function HolidayEditor({ calendarId, language }: { calendarId: string; language: string }) {
  const { t } = useTranslation();
  const holidays = useHolidays(calendarId);
  const create = useCreateHoliday(calendarId);
  const remove = useDeleteHoliday(calendarId);

  const [day, setDay] = useState('');
  const [name, setName] = useState('');
  const [recurring, setRecurring] = useState(true);

  return (
    <>
      <div className="field">
        <label className="field__label" htmlFor="holiday-day">
          {t('sla.holidayDate')}
        </label>
        <input
          id="holiday-day"
          type="date"
          className="field__control"
          value={day}
          onChange={(event) => {
            setDay(event.target.value);
          }}
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="holiday-name">
          {t('masterdata.name')}
        </label>
        <input
          id="holiday-name"
          className="field__control"
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
      </div>

      <label className="field__checkbox">
        <input
          type="checkbox"
          checked={recurring}
          onChange={(event) => {
            setRecurring(event.target.checked);
          }}
        />
        {t('sla.recurringYearly')}
      </label>

      {create.isError ? <ErrorNotice error={create.error} /> : null}

      <Button
        variant="secondary"
        disabled={!day || !name.trim()}
        busy={create.isPending}
        onClick={() => {
          create.mutate(
            { day, name: name.trim(), recurring_yearly: recurring },
            {
              onSuccess: () => {
                setDay('');
                setName('');
              },
            },
          );
        }}
      >
        {t('masterdata.add')}
      </Button>

      <dl className="definition-list" style={{ marginTop: 'var(--space-4)' }}>
        {(holidays.data ?? []).map((holiday) => (
          <div key={holiday.id} className="history__change">
            <dt>{holiday.name}</dt>
            <dd>
              {holiday.recurring_yearly
                ? t('sla.everyYearOn', { date: formatDate(holiday.day, language) })
                : formatDate(holiday.day, language)}{' '}
              <Button
                variant="ghost"
                onClick={() => {
                  remove.mutate(holiday.id);
                }}
              >
                {t('masterdata.delete')}
              </Button>
            </dd>
          </div>
        ))}
      </dl>
    </>
  );
}
